import { useCallback, useEffect, useRef, useState } from 'react'
import { useRecoilValue } from 'recoil'
import { EDITOR_PAGE_CONTENT_STALL_TIMEOUT_MS } from '@/api/editorPageContent'
import {
  bleedCheckCompletedAtom,
  hasBleedWarningsSelector,
} from '@/atoms/bleedWarningAtoms'

/**
 * How long Back waits for an unfinished margin check before it carries on
 * without one: the page-content requests' stall timeout. A walk that cannot
 * read a page gives no verdict at all, so without a bound one failed or
 * stalled read would block leaving. It bounds the wait and delays nothing:
 * Back resolves the moment the check finishes.
 */
export const BACK_BLEED_CHECK_WAIT_LIMIT_MS =
  EDITOR_PAGE_CONTENT_STALL_TIMEOUT_MS

/**
 * How a waiting Back ended: the finished check found text ink in the cyan
 * band, found none, or the bound passed first.
 */
export type BackBleedCheckOutcome = 'bleeding' | 'clear' | 'timedOut'

type PendingBack = { startedAt: number; expired: boolean }

type UseBackBleedCheckWaitArgs = {
  /**
   * Called once per wait, with how long Back waited. A wait ended with
   * `cancel` is never resolved.
   */
  onResolved: (outcome: BackBleedCheckOutcome, waitedMs: number) => void
}

type UseBackBleedCheckWaitResult = {
  /** Back is waiting for the check's verdict. */
  waiting: boolean
  /**
   * Back was pressed while the check has no verdict yet. A press while Back
   * already waits joins that wait.
   */
  start: () => void
  /** The person closed the wait: it ends with no verdict and Back stays put. */
  cancel: () => void
}

/**
 * Back pressed while the all-pages margin check is still running waits for
 * that check's verdict instead of leaving unchecked. `bleedCheckCompletedAtom`
 * turns true when a walk over the loaded pages finishes, and
 * `hasBleedWarningsSelector` is its verdict, so a wait started while the atom
 * is false ends on a walk that finished after the press.
 */
export const useBackBleedCheckWait = ({
  onResolved,
}: UseBackBleedCheckWaitArgs): UseBackBleedCheckWaitResult => {
  const checkCompleted = useRecoilValue(bleedCheckCompletedAtom)
  const hasBleedWarnings = useRecoilValue(hasBleedWarningsSelector)
  const [pendingBack, setPendingBack] = useState<PendingBack | null>(null)
  // What happens after the wait reads state that moves while Back waits (the
  // image-quality check's), so the verdict goes to the latest handler.
  const onResolvedRef = useRef(onResolved)
  useEffect(() => {
    onResolvedRef.current = onResolved
  })

  useEffect(() => {
    if (pendingBack === null || pendingBack.expired) return
    const timeoutId = setTimeout(
      () =>
        setPendingBack((current) =>
          current === null ? null : { ...current, expired: true },
        ),
      BACK_BLEED_CHECK_WAIT_LIMIT_MS,
    )
    return () => clearTimeout(timeoutId)
  }, [pendingBack])

  useEffect(() => {
    if (pendingBack === null) return
    if (!checkCompleted && !pendingBack.expired) return
    setPendingBack(null)
    const outcome: BackBleedCheckOutcome = !checkCompleted
      ? 'timedOut'
      : hasBleedWarnings
        ? 'bleeding'
        : 'clear'
    onResolvedRef.current(outcome, Date.now() - pendingBack.startedAt)
  }, [pendingBack, checkCompleted, hasBleedWarnings])

  // A second press while Back already waits (the Back button keeps focus
  // behind the modal, so Enter reaches it) joins the wait in progress rather
  // than restarting its bound.
  const start = useCallback(
    () =>
      setPendingBack(
        (current) => current ?? { startedAt: Date.now(), expired: false },
      ),
    [],
  )
  const cancel = useCallback(() => setPendingBack(null), [])

  return { waiting: pendingBack !== null, start, cancel }
}
